import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { dashboardAPI } from '../../services/api'
import { useAutoRefresh } from '../../hooks/useAutoRefresh'
import { ONBOARDING_VIDEOS_CHECK_EVENT } from '../../constants/onboardingVideos'
import ProductMuxIframe from '../common/ProductMuxIframe'
import { DASHBOARD_MUX_THUMBNAIL_TIME_SEC } from '../../constants/dashboardMuxVideo'

const MUX_IFRAME_SRC = `https://player.mux.com/rkQBK3g5KIi007PyETBukvU2qh3B27a3qVeYYOVp6254?metadata-video-title=First+Meeting+Created&video-title=First+Meeting+Created&thumbnail-time=${DASHBOARD_MUX_THUMBNAIL_TIME_SEC}`

/**
 * One-time Mux welcome after the user’s first non-draft meeting is created in the dashboard.
 * Mounted only under `ProductAppVideoBoundary` in App.jsx on product-app routes.
 */
export default function FirstMeetingCreatedVideo() {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [pollingDone, setPollingDone] = useState(false)

  const fetchStatus = useCallback(async () => {
    if (!user || pollingDone) return
    try {
      const raw = await dashboardAPI.getFirstMeetingCreatedVideo()
      const payload = raw?.data ?? raw
      if (payload?.showFirstMeetingCreatedVideo) {
        setOpen(true)
      }
    } catch {
      /* non-fatal */
    }
  }, [user, pollingDone])

  useEffect(() => {
    if (!user) return
    fetchStatus()
  }, [user, fetchStatus])

  useEffect(() => {
    const onCheck = () => fetchStatus()
    window.addEventListener(ONBOARDING_VIDEOS_CHECK_EVENT, onCheck)
    return () => window.removeEventListener(ONBOARDING_VIDEOS_CHECK_EVENT, onCheck)
  }, [fetchStatus])

  useAutoRefresh(fetchStatus, {
    intervalMs: 90_000,
    staleTtlMs: 45_000,
    enabled: Boolean(user) && !pollingDone && !open,
  })

  const dismiss = useCallback(async () => {
    setOpen(false)
    try {
      await dashboardAPI.ackFirstMeetingCreatedVideo()
      setPollingDone(true)
    } catch {
      /* keep polling */
    }
  }, [])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === 'Escape') dismiss()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, dismiss])

  if (!user) return null

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[96] flex items-center justify-center bg-black/50 backdrop-blur-sm p-3 sm:p-6"
          role="presentation"
          onClick={dismiss}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="first-meeting-created-video-title"
            initial={{ opacity: 0, scale: 0.97, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 8 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="relative w-full max-w-[calc(100vw-1rem)] sm:max-w-[min(92vw,560px)] md:max-w-[min(720px,33vw)] rounded-2xl bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
              <h2 id="first-meeting-created-video-title" className="text-base font-semibold text-gray-900">
                Your first meeting is created
              </h2>
              <button
                type="button"
                onClick={dismiss}
                className="rounded-lg p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
                aria-label="Close video"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="bg-black">
              <ProductMuxIframe
                src={MUX_IFRAME_SRC}
                className="w-full border-0 aspect-video block"
                title="First meeting created"
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
